import { Avatar, Flex, Text } from "@fluentui/react-northstar";
import React from "react";
import styled from "styled-components";

/**
 * @typedef {Object} Comment
 * @property {string} user_fullname - The full name of the user who made the comment.
 * @property {string} user_avatar - The URL of the user's avatar image.
 * @property {string} date - The date when the comment was made.
 * @property {string} type - The type of the comment (e.g., "expense_edited").
 * @property {string} description - The description or content of the comment.
 */

/**
 * OneComment component displays a single comment with user details and comment content.
 *
 * @param {Object} props - The props object.
 * @param {Comment} props.comment - The comment data to display.
 * @returns {JSX.Element} The rendered OneComment component.
 */
const OneComment = ({ comment, isDescriptionComponent = false }) => {
    return (
        <Wrapper vAlign="center" fill gap="gap.medium" column>
            <Flex gap="gap.small" vAlign="center">
                <Flex vAlign="center" gap="gap.small">
                    <Avatar
                        name={comment.user_fullname}
                        image={comment.user_avatar}
                    />
                    <STextFullname content={comment.user_fullname} />
                </Flex>
                <STextDate content={comment.date} />
            </Flex>
            <WrapperComment fill>
                {!isDescriptionComponent && (
                    <Comment
                        content={`${
                            comment.type === "expense_edited"
                                ? "Modification"
                                : ""
                        } ${comment.description}`}
                    />
                )}
                {isDescriptionComponent && comment.description}
            </WrapperComment>
        </Wrapper>
    );
};

export default OneComment;

const Comment = styled(Text)`
    color: #242424;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
`;

const STextDate = styled(Text)`
    color: var(--Light-theme-Rest-Foreground-Default-Foreground-2, #616161);

    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; /* 133.333% */
`;

const STextFullname = styled(Text)`
    color: var(
        --light-theme-rest-foreground-default-foreground-242424,
        #242424
    );

    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
`;

const Wrapper = styled(Flex)`
    padding: 8px 8px 16px 8px;

    border-radius: 4px;
    background: var(
        --Light-theme-Rest-Background-Default-Background-1,
        #fafafa
    );

    /* shadow-4 */
    box-shadow: 0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.07),
        0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.11);
`;

const WrapperComment = styled(Flex)`
    background: var(--Light-theme-Rest-Background-Default-Background, #fff);
    padding: 6px 12px 10px 12px;
    border-radius: 4px;
    box-shadow: 0px 0.15px 0.45px 0px rgba(0, 0, 0, 0.11),
        0px 0.8px 1.8px 0px rgba(0, 0, 0, 0.13);
`;

const EvolutionTitleComment = styled(Text)`
    font-style: normal;
    font-weight: 600;
    font-size: clamp(14px, 2vw, 18px);
    line-height: clamp(18px, 2vw, 24px);
    color: #424242;
`;
